import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    CheckIcon,
    ArrowRightIcon,
    UserGroupIcon,
    ChartBarIcon,
    CreditCardIcon,
    ClockIcon,
    SparklesIcon,
    RocketLaunchIcon,
    CalendarDaysIcon,
    TicketIcon,
    MagnifyingGlassIcon,
    StarIcon,
    LockClosedIcon,
    BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import config from '../config';

const Plans = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [voucherCode, setVoucherCode] = useState('');
    const [voucherDiscount, setVoucherDiscount] = useState(0);
    const [voucherValid, setVoucherValid] = useState(false);
    const [voucherLabel, setVoucherLabel] = useState('');
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);

    // Determine initial tab: default 'venue', switch to 'directory' if coming from find-pt or explicit ?tab=directory
    const fromFindPt = searchParams.get('from') === 'find-pt';
    const tabParam = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState(
        (fromFindPt || tabParam === 'directory') ? 'directory' : 'venue'
    );

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await fetch(`${config.apiBaseUrl}/api/Subscription/venue-platform-plans`);
                if (response.ok) {
                    const data = await response.json();
                    const plansWithIcons = data.map(plan => ({
                        ...plan,
                        description: getDescriptionForPlan(plan.id),
                        popular: plan.id === 'medium',
                        icon: getIconForPlan(plan.id)
                    }));
                    setPlans(plansWithIcons);
                }
            } catch (error) {
                console.error('Error fetching plans:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPlans();
    }, []);

    const getDescriptionForPlan = (planId) => {
        const descriptions = {
            'pt-directory-founder': 'Locked-in founder rate — get listed and get found for life',
            'pt-directory': 'Get listed in the WorldsHighStreet PT Directory',
            'basic': 'Perfect for starting your personal training business',
            'medium': 'Great for growing training businesses',
            'large': 'Ideal for established training operations',
            'ultimate': 'For large-scale training businesses'
        };
        return descriptions[planId] || '';
    };

    const getIconForPlan = (planId) => {
        const icons = {
            'pt-directory-founder': StarIcon,
            'pt-directory': MagnifyingGlassIcon,
            'basic': UserGroupIcon,
            'medium': ChartBarIcon,
            'large': RocketLaunchIcon,
            'ultimate': SparklesIcon
        };
        return icons[planId] || UserGroupIcon;
    };

    useEffect(() => {
        const voucherFromUrl = searchParams.get('voucher');
        if (voucherFromUrl) {
            validateVoucher(voucherFromUrl);
        }
    }, [searchParams]);

    const validateVoucher = async (code) => {
        if (!code || code.trim() === '') {
            setVoucherCode(''); setVoucherDiscount(0); setVoucherValid(false); setVoucherLabel('');
            return;
        }
        try {
            const response = await fetch(`${config.apiBaseUrl}/api/Subscription/validate-voucher`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ voucherCode: code })
            });
            if (response.ok) {
                const data = await response.json();
                if (data.isValid) {
                    setVoucherCode(code); setVoucherDiscount(data.discount);
                    setVoucherValid(true); setVoucherLabel(data.discountLabel);
                } else {
                    setVoucherCode(''); setVoucherDiscount(0); setVoucherValid(false); setVoucherLabel('');
                }
            } else {
                setVoucherCode(''); setVoucherDiscount(0); setVoucherValid(false); setVoucherLabel('');
            }
        } catch (error) {
            console.error('Error validating voucher:', error);
            setVoucherCode(''); setVoucherDiscount(0); setVoucherValid(false); setVoucherLabel('');
        }
    };

    const calculateDiscountedPrice = (plan) => {
        if (plan.isLifetimePricing) return plan.price.toFixed(2);
        if (!voucherValid) return plan.price.toFixed(2);
        return (plan.price * (1 - voucherDiscount)).toFixed(2);
    };

    const handleSelectPlan = (planId) => {
        const params = new URLSearchParams({ plan: planId });
        if (voucherValid && voucherCode) params.append('voucher', voucherCode);
        navigate(`/signup/venue-admin?${params.toString()}`);
    };

    const directoryPlans = plans.filter(p => p.isPtDirectoryPlan);
    const venuePlans = plans.filter(p => !p.isPtDirectoryPlan);

    const renderPlanCard = (plan) => {
        const Icon = plan.icon;
        const displayPrice = calculateDiscountedPrice(plan);
        const showVoucherStrike = voucherValid && !plan.isLifetimePricing && plan.price.toFixed(2) !== displayPrice;

        return (
            <div
                key={plan.id}
                className={`card-luxury p-6 sm:p-8 relative flex flex-col ${
                    plan.popular ? 'border-2 border-emerald-600/50 shadow-luxury-lg'
                    : plan.isFounderPlan ? 'border-2 border-amber-500/60 shadow-luxury-lg'
                    : ''
                }`}
            >
                {plan.popular && !plan.isFounderPlan && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-luxury-black text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                            MOST POPULAR
                        </div>
                    </div>
                )}
                {plan.isFounderPlan && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <div className="bg-gradient-to-r from-amber-500 to-amber-400 text-luxury-black text-xs font-bold px-4 py-1 rounded-full whitespace-nowrap">
                            ⭐ FOUNDER RATE – LIMITED SPOTS
                        </div>
                    </div>
                )}

                <div className="text-center mb-6">
                    <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl mb-4 ${plan.isFounderPlan ? 'bg-amber-500/10' : 'bg-emerald-600/10'}`}>
                        <Icon className={`h-7 w-7 ${plan.isFounderPlan ? 'text-amber-400' : 'text-emerald-300'}`} />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-luxury-pearl mb-2">{plan.name}</h3>
                    <p className="text-sm text-luxury-pearl/60 mb-4">{plan.description}</p>

                    {plan.isLifetimePricing && (
                        <div className="inline-flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/40 text-amber-400 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                            <LockClosedIcon className="h-3.5 w-3.5" />
                            Lifetime Pricing
                        </div>
                    )}

                    {showVoucherStrike && (
                        <div className="mb-2">
                            <span className="text-lg text-luxury-pearl/40 line-through">£{plan.price.toFixed(2)}</span>
                            <span className="ml-2 text-xs bg-emerald-600/20 text-emerald-300 px-2 py-1 rounded-full font-bold">{voucherLabel}</span>
                        </div>
                    )}

                    <div className="flex items-baseline justify-center gap-1">
                        <span className={`text-4xl font-display font-bold ${plan.isFounderPlan ? 'text-amber-400' : 'text-emerald-400'}`}>
                            £{displayPrice}
                        </span>
                        <span className="text-luxury-pearl/60 text-sm">/month</span>
                    </div>

                    {plan.isLifetimePricing && (
                        <p className="text-amber-400/80 text-xs mt-1 font-medium">This price never changes</p>
                    )}

                    {plan.isFounderPlan && plan.founderNote && (
                        <p className="text-amber-300/70 text-xs mt-2 italic px-2">{plan.founderNote}</p>
                    )}
                </div>

                <div className="space-y-3 mb-8 flex-1">
                    {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                            <CheckIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${plan.isFounderPlan ? 'text-amber-400' : 'text-emerald-400'}`} />
                            <span className="text-luxury-pearl/70 text-sm">{feature}</span>
                        </div>
                    ))}
                </div>

                <button
                    onClick={() => handleSelectPlan(plan.id)}
                    className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center gap-2 group ${
                        plan.isFounderPlan
                            ? 'bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-luxury-black'
                            : plan.popular
                            ? 'btn-primary'
                            : 'border border-emerald-600/30 hover:border-emerald-600/60 bg-emerald-600/5 hover:bg-emerald-600/10 text-emerald-300'
                    }`}
                >
                    Get Started
                    <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-luxury-black">
            {/* Navigation */}
            <nav className="border-b border-luxury-graphite/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate('/')}>
                            <h1 className="text-xl sm:text-2xl font-display font-bold text-gradient-emerald">WorldsHighStreet</h1>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-4">
                            <button onClick={() => navigate('/')} className="text-emerald-300/80 hover:text-emerald-300 transition-colors text-sm sm:text-base font-medium">Home</button>
                            <button onClick={() => navigate('/marketing')} className="text-emerald-300/80 hover:text-emerald-300 transition-colors text-sm sm:text-base font-medium">Features</button>
                            <a href="/login" className="text-emerald-300/80 hover:text-emerald-300 transition-colors text-sm sm:text-base font-medium">Login</a>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 via-transparent to-emerald-600/5"></div>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 relative">
                    <div className="text-center max-w-3xl mx-auto">
                        <h1 className="text-4xl sm:text-5xl font-display font-bold text-luxury-pearl mb-4">
                            Simple, Transparent <span className="text-emerald-400">Pricing</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-luxury-pearl/60 mb-6">
                            Choose the plan that fits your business. No hidden fees, no surprises.
                        </p>
                        {voucherValid && (
                            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600/20 to-emerald-500/20 border border-emerald-500/50 rounded-full px-6 py-3">
                                <TicketIcon className="h-6 w-6 text-emerald-300" />
                                <span className="text-emerald-300 font-bold text-lg">{voucherLabel} Applied!</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Tab Switcher ── */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
                <div className="flex justify-center">
                    <div className="inline-flex bg-luxury-graphite/30 border border-luxury-graphite/50 rounded-xl p-1 gap-1">
                        <button
                            onClick={() => setActiveTab('directory')}
                            className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                                activeTab === 'directory'
                                    ? 'bg-amber-500 text-luxury-black shadow-lg'
                                    : 'text-luxury-pearl/60 hover:text-luxury-pearl hover:bg-luxury-graphite/40'
                            }`}
                        >
                            <MagnifyingGlassIcon className="h-4 w-4" />
                            PT Directory
                        </button>
                        <button
                            onClick={() => setActiveTab('venue')}
                            className={`flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                                activeTab === 'venue'
                                    ? 'bg-emerald-600 text-luxury-black shadow-lg'
                                    : 'text-luxury-pearl/60 hover:text-luxury-pearl hover:bg-luxury-graphite/40'
                            }`}
                        >
                            <BuildingStorefrontIcon className="h-4 w-4" />
                            Venue Plans
                        </button>
                    </div>
                </div>

                {/* Tab description */}
                <div className="text-center mt-4">
                    {activeTab === 'directory' ? (
                        <p className="text-luxury-pearl/50 text-sm max-w-lg mx-auto">
                            Just want to be found by clients? Join the PT Directory — no venue management needed.
                            Both plans include <span className="text-amber-400 font-semibold">lifetime pricing</span> locked in from day one.
                        </p>
                    ) : (
                        <p className="text-luxury-pearl/50 text-sm max-w-lg mx-auto">
                            Full venue management — sessions, tokens, payments and reporting.
                            All venue plans include a <span className="text-emerald-400 font-semibold">free PT Directory listing</span>.
                        </p>
                    )}
                </div>
            </div>

            {/* ── Plan Cards ── */}
            {loading ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                </div>
            ) : (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                    {activeTab === 'directory' ? (
                        <>
                            {/* PT Directory tab header */}
                            {/*<div className="text-center mb-10">*/}
                            {/*    <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 mb-4">*/}
                            {/*        <MagnifyingGlassIcon className="h-4 w-4 text-amber-400" />*/}
                            {/*        <span className="text-amber-400 text-sm font-semibold">GET LISTED. GET FOUND.</span>*/}
                            {/*    </div>*/}
                            {/*</div>*/}
                            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 lg:gap-8 max-w-3xl mx-auto">
                                {directoryPlans.map(plan => (
                                    <React.Fragment key={plan.id}>{renderPlanCard(plan)}</React.Fragment>
                                ))}
                            </div>

                            {/* Directory FAQ */}
                            <div className="mt-16 max-w-3xl mx-auto space-y-4">
                                <h2 className="text-xl font-display font-bold text-luxury-pearl text-center mb-6">About the PT Directory</h2>
                                <div className="card-luxury p-5">
                                    <h3 className="text-sm font-semibold text-luxury-pearl mb-1">What is lifetime pricing?</h3>
                                    <p className="text-luxury-pearl/60 text-sm">Your monthly rate is locked in forever — it will never increase as long as you remain subscribed. The Foundation Member rate (£1.99/month) is exclusively for the first 30 PTs to join.</p>
                                </div>
                                <div className="card-luxury p-5">
                                    <h3 className="text-sm font-semibold text-luxury-pearl mb-1">What do I get in the directory?</h3>
                                    <p className="text-luxury-pearl/60 text-sm">Your profile is listed publicly on the WorldsHighStreet Find a PT page. Potential clients can discover you, view your specialisms, and send you an enquiry directly.</p>
                                </div>
                                <div className="card-luxury p-5">
                                    <h3 className="text-sm font-semibold text-luxury-pearl mb-1">Can I upgrade to a venue plan later?</h3>
                                    <p className="text-luxury-pearl/60 text-sm">Yes — all venue plans already include a free PT Directory listing, so you can move up whenever you're ready.</p>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Venue Plans tab */}
                            {voucherValid && (
                                <p className="text-center text-emerald-300/70 text-sm mb-8">
                                    Voucher <span className="font-bold text-emerald-300">{voucherLabel}</span> applied to all venue plans below.
                                </p>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                                {venuePlans.map(plan => (
                                    <React.Fragment key={plan.id}>{renderPlanCard(plan)}</React.Fragment>
                                ))}
                            </div>

                            {/* Voucher Input */}
                            {!voucherValid && (
                                <div className="mt-10">
                                    <div className="card-luxury p-6 max-w-md mx-auto">
                                        <div className="flex items-center gap-2 mb-3">
                                            <TicketIcon className="h-5 w-5 text-emerald-300" />
                                            <h3 className="text-lg font-semibold text-luxury-pearl">Have a Voucher Code?</h3>
                                        </div>
                                        <p className="text-luxury-pearl/50 text-xs mb-3">Vouchers apply to venue management plans only.</p>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={voucherCode}
                                                onChange={(e) => setVoucherCode(e.target.value)}
                                                onKeyDown={(e) => e.key === 'Enter' && validateVoucher(voucherCode)}
                                                placeholder="Enter voucher code"
                                                className="flex-1 px-4 py-2 rounded-lg bg-luxury-graphite/30 border border-emerald-600/30 text-luxury-pearl placeholder-luxury-pearl/40 focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                                            />
                                            <button
                                                onClick={() => validateVoucher(voucherCode)}
                                                className="bg-emerald-600 hover:bg-emerald-500 text-luxury-black rounded-lg px-6 py-2 font-semibold transition-all duration-200"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Venue features grid */}
                            <div className="mt-16 card-luxury p-8 sm:p-12">
                                <h2 className="text-2xl sm:text-3xl font-display font-bold text-luxury-pearl text-center mb-8">All Venue Plans Include</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {[
                                        { icon: CalendarDaysIcon, title: 'Unlimited Sessions', desc: 'Create as many training sessions as you need' },
                                        { icon: UserGroupIcon, title: 'Client Token System', desc: 'Flexible booking with client tokens' },
                                        { icon: CreditCardIcon, title: 'GoCardless Integration', desc: 'Automated payment collection' },
                                        { icon: ChartBarIcon, title: 'Reporting & Analytics', desc: 'Track attendance and engagement' },
                                        { icon: ClockIcon, title: '24/7 Client Booking', desc: 'Clients book anytime, anywhere' },
                                        { icon: MagnifyingGlassIcon, title: 'Free PT Directory Listing', desc: 'All venue plans include a free directory entry' },
                                        { icon: CheckIcon, title: 'Mobile Friendly', desc: 'Manage on the go from any device' },
                                    ].map(({ icon: Icon, title, desc }) => (
                                        <div key={title} className="flex items-start gap-3">
                                            <div className="flex-shrink-0 w-10 h-10 bg-emerald-600/10 rounded-lg flex items-center justify-center">
                                                <Icon className="h-5 w-5 text-emerald-300" />
                                            </div>
                                            <div>
                                                <h3 className="text-luxury-pearl font-semibold mb-1">{title}</h3>
                                                <p className="text-luxury-pearl/60 text-sm">{desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Venue FAQ */}
                            <div className="mt-16 max-w-4xl mx-auto space-y-4">
                                <h2 className="text-2xl font-display font-bold text-luxury-pearl text-center mb-6">Frequently Asked Questions</h2>
                                {[
                                    { q: 'Can I change my plan later?', a: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.' },
                                    { q: 'What counts as an "active user"?', a: 'An active user is a client who has tokens and can book sessions. You have full control over who is active.' },
                                    { q: 'How does payment processing work?', a: 'We integrate with GoCardless to handle all subscription payments automatically. Your clients\' payments are collected seamlessly every month.' },
                                    { q: 'Is there a setup fee?', a: 'No setup fees. Just choose your plan and get started. The price you see is the price you pay.' },
                                ].map(({ q, a }) => (
                                    <div key={q} className="card-luxury p-6">
                                        <h3 className="text-lg font-semibold text-luxury-pearl mb-2">{q}</h3>
                                        <p className="text-luxury-pearl/60">{a}</p>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* CTA */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
                <div className="card-luxury p-8 sm:p-12 text-center bg-gradient-to-br from-emerald-600/10 to-transparent">
                    <h2 className="text-3xl sm:text-4xl font-display font-bold text-luxury-pearl mb-4">Ready to Get Started?</h2>
                    <p className="text-lg text-luxury-pearl/60 mb-8 max-w-2xl mx-auto">
                        Choose your plan and start your personal training business in minutes.
                    </p>
                    <button
                        onClick={() => navigate('/marketing')}
                        className="border border-emerald-600/30 hover:border-emerald-600/60 bg-emerald-600/5 hover:bg-emerald-600/10 text-emerald-300 font-semibold px-8 py-3 rounded-lg transition-all duration-200"
                    >
                        Learn More About Features
                    </button>
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-luxury-graphite/30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <p className="text-luxury-pearl/40 text-sm">© 2026 WorldsHighStreet. Empowering Personal Training Businesses.</p>
                        <div className="flex gap-6">
                            <button onClick={() => navigate('/')} className="text-luxury-pearl/40 hover:text-emerald-300 text-sm transition-colors">Home</button>
                            <button onClick={() => navigate('/marketing')} className="text-luxury-pearl/40 hover:text-emerald-300 text-sm transition-colors">Features</button>
                            <a href="/login" className="text-luxury-pearl/40 hover:text-emerald-300 text-sm transition-colors">Login</a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Plans;

